// src/components/VouchersDatabase.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_API_URL || "http://localhost:3004";

const VouchersDatabase = ({ token }) => {
  const [vouchers, setVouchers] = useState([]);
  const [pagination, setPagination] = useState({});
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedItems, setExpandedItems] = useState(new Set());

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // Zwiększam czas debounce

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchVouchers = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 20,
        search: debouncedSearchTerm,
        status: statusFilter
      };

      const response = await axios.get(`${API_URL}/vouchersList/database/list`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params
      });

      setVouchers(response.data.vouchers);
      setPagination(response.data.pagination);
    } catch (err) {
      console.error('Error fetching vouchers from database:', err);
      if (err.response?.status !== 401) { // Nie ustawiaj błędu dla błędów autoryzacji
        setError('Nie udało się pobrać danych voucherów.');
      }
    } finally {
      setLoading(false);
    }
  }, [token, currentPage, debouncedSearchTerm, statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/vouchersList/database/stats`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching voucher stats:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchVouchers();
  }, [fetchVouchers]);

  useEffect(() => {
    // Pobieramy statystyki tylko przy pierwszym ładowaniu
    if (currentPage === 1 && !debouncedSearchTerm && !statusFilter) {
      fetchStats();
    }
  }, [fetchStats, currentPage, debouncedSearchTerm, statusFilter]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const toggleExpanded = (voucherId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(voucherId)) {
      newExpanded.delete(voucherId);
    } else {
      newExpanded.add(voucherId);
    }
    setExpandedItems(newExpanded);
  };

  const handleViewFile = async (filename) => {
    try {
      const response = await axios.get(`${API_URL}/vouchersList/file/${encodeURIComponent(filename)}`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        responseType: 'blob'
      });

      const fileURL = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(fileURL, '_blank');
    } catch (err) {
      console.error('Error fetching voucher file:', err);
      alert('Nie udało się pobrać pliku voucheru.');
    }
  };

  const handleDownloadFile = async (filename) => {
    try {
      const response = await axios.get(`${API_URL}/vouchersList/file/${encodeURIComponent(filename)}`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (err) {
      console.error('Error downloading voucher file:', err);
      alert('Nie udało się pobrać pliku voucheru.');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pl-PL');
  };

  const getStatusBadge = (status, isExpired, daysUntilExpiry) => {
    let bgColor, textColor, text;
    
    if (status === 'used') {
      bgColor = 'bg-purple-100';
      textColor = 'text-purple-800';
      text = 'Wykorzystany';
    } else if (status === 'inactive') {
      bgColor = 'bg-orange-100';
      textColor = 'text-orange-800';
      text = 'Nieaktywny';
    } else if (isExpired || status === 'expired') {
      bgColor = 'bg-red-100';
      textColor = 'text-red-800';
      text = 'Wygasły';
    } else {
      bgColor = 'bg-green-100';
      textColor = 'text-green-800';
      text = 'Aktywny';
    }

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${bgColor} ${textColor}`}>
        {text}
      </span>
    );
  };

  const getDaysText = (daysUntilExpiry) => {
    if (daysUntilExpiry < 0) {
      return `wygasł ${Math.abs(daysUntilExpiry)} dni temu`;
    } else if (daysUntilExpiry === 0) {
      return 'wygasa dzisiaj';
    } else if (daysUntilExpiry === 1) {
      return 'wygasa jutro';
    } else {
      return `wygasa za ${daysUntilExpiry} dni`;
    }
  };

  if (loading && vouchers.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8 shadow-lg">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Ładowanie voucherów...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 max-w-md w-full">
          <div className="flex items-start space-x-3">
            <svg className="w-6 h-6 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        
        {/* Header */}
        <div className="text-center mb-8 pt-12">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Baza danych voucherów</h2>
          <p className="text-gray-600 text-sm">Szczegółowy przegląd wszystkich voucherów</p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{stats.total || 0}</p>
            <p className="text-xs text-gray-600">Wszystkie</p>
          </div>
          <div className="bg-white rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{stats.active || 0}</p>
            <p className="text-xs text-gray-600">Aktywne</p>
          </div>
          <div className="bg-white rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-purple-600">{stats.used || 0}</p>
            <p className="text-xs text-gray-600">Użyte</p>
          </div>
          <div className="bg-white rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-red-600">{stats.expired || 0}</p>
            <p className="text-xs text-gray-600">Wygasłe</p>
          </div>
          <div className="bg-white rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-orange-600">{stats.inactive || 0}</p>
            <p className="text-xs text-gray-600">Nieaktywne</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Szukaj po kodzie, emailu, nazwisku, telefonie..."
                  value={searchTerm}
                  onChange={handleSearch}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                    }
                  }}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
            
            {/* Status filters */}
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => handleStatusFilter('')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === '' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Wszystkie
              </button>
              <button
                onClick={() => handleStatusFilter('active')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === 'active' ? 'bg-green-600 text-white' : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
              >
                Aktywne
              </button>
              <button
                onClick={() => handleStatusFilter('used')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === 'used' ? 'bg-purple-600 text-white' : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                }`}
              >
                Użyte
              </button>
              <button
                onClick={() => handleStatusFilter('expired')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === 'expired' ? 'bg-red-600 text-white' : 'bg-red-100 text-red-700 hover:bg-red-200'
                }`}
              >
                Wygasłe
              </button>
            </div>
          </div>
        </div>

        {/* Results Info */}
        <div className="text-center mb-4">
          <p className="text-sm text-gray-600">
            Pokazano {vouchers.length} z {pagination.totalItems || 0} voucherów
            {debouncedSearchTerm && ` (filtrowane)`}
          </p>
        </div>

        {/* Vouchers List */}
        {vouchers.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 text-center">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-gray-500 text-lg">
              {debouncedSearchTerm || statusFilter ? 'Brak voucherów pasujących do kryteriów' : 'Brak voucherów w bazie danych'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {vouchers.map((voucher) => (
              <div key={voucher.id} className="bg-white rounded-2xl shadow-lg border border-gray-100">
                <div className="p-4">
                  <div className="space-y-3">
                    {/* Główna informacja o voucherze */}
                    <div className="flex items-center space-x-4">
                      <div className="bg-purple-100 p-2 rounded-xl flex-shrink-0">
                        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 mb-1">
                          <h3 className="font-bold text-gray-800 truncate">{voucher.code}</h3>
                          <div className="mt-1 sm:mt-0">
                            {getStatusBadge(voucher.status, voucher.isExpired, voucher.daysUntilExpiry)}
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 text-sm text-gray-600">
                          <span className="truncate">{voucher.full_name}</span>
                          <span className="truncate">{voucher.product_name}</span>
                          <div>
                            <div className="font-medium">{voucher.product_price} zł</div>
                            {voucher.remaining_value !== undefined && (
                              <div className="text-xs text-gray-500">Pozostało: {voucher.remaining_value} zł</div>
                            )}
                            {voucher.priceDiscrepancy && (
                              <div className="text-xs text-orange-600 font-medium">
                                ⚠️ Prawdziwa wartość: {voucher.priceDiscrepancy.correctUnitPrice} zł
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Przyciski akcji - zawsze pod spodem */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2 border-t border-gray-100">
                      <div className="order-2 sm:order-1">
                        {voucher.hasPdfFile && (
                          <div className="flex flex-col xs:flex-row gap-2">
                            <button
                              onClick={() => handleViewFile(voucher.pdfFilename)}
                              className="flex-1 xs:flex-none bg-blue-100 text-blue-600 px-4 py-2 rounded-xl hover:bg-blue-200 transition-colors flex items-center justify-center space-x-2"
                              title="Otwórz PDF"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              <span className="text-sm font-medium">Otwórz PDF</span>
                            </button>
                            <button
                              onClick={() => handleDownloadFile(voucher.pdfFilename)}
                              className="flex-1 xs:flex-none bg-green-100 text-green-600 px-4 py-2 rounded-xl hover:bg-green-200 transition-colors flex items-center justify-center space-x-2"
                              title="Pobierz PDF"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                              </svg>
                              <span className="text-sm font-medium">Pobierz PDF</span>
                            </button>
                          </div>
                        )}
                        {!voucher.hasPdfFile && (
                          <span className="text-sm text-gray-500 italic">Brak pliku PDF</span>
                        )}
                      </div>
                      <div className="order-1 sm:order-2">
                        <button
                          onClick={() => toggleExpanded(voucher.id)}
                          className="w-full sm:w-auto bg-gray-100 text-gray-600 px-4 py-2 rounded-xl hover:bg-gray-200 transition-colors flex items-center justify-center space-x-2"
                        >
                          <span className="text-sm font-medium">
                            {expandedItems.has(voucher.id) ? 'Zwiń szczegóły' : 'Pokaż szczegóły'}
                          </span>
                          <svg 
                            className={`w-4 h-4 transition-transform ${expandedItems.has(voucher.id) ? 'rotate-180' : ''}`}
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedItems.has(voucher.id) && (
                  <div className="border-t border-gray-100 p-4 bg-gray-50">
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 text-sm">
                        <div className="bg-white rounded-lg p-3">
                          <h4 className="font-semibold text-gray-700 mb-2">Dane klienta</h4>
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Email:</span>
                              <span className="font-medium text-right break-all">{voucher.email}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Telefon:</span>
                              <span className="font-medium text-right">{voucher.phone || 'Brak'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Imię i nazwisko:</span>
                              <span className="font-medium text-right">{voucher.full_name}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="bg-white rounded-lg p-3">
                          <h4 className="font-semibold text-gray-700 mb-2">Szczegóły produktu</h4>
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Produkt:</span>
                              <span className="font-medium text-right">{voucher.product_name}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">ID produktu:</span>
                              <span className="font-medium text-right">{voucher.product_id}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Cena zapisana:</span>
                              <span className={`font-medium text-right ${voucher.priceDiscrepancy ? 'line-through text-gray-400' : ''}`}>
                                {voucher.product_price} zł
                              </span>
                            </div>
                            {voucher.priceDiscrepancy && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Prawdziwa wartość:</span>
                                <span className="font-medium text-right text-green-600">
                                  {voucher.priceDiscrepancy.correctUnitPrice} zł
                                </span>
                              </div>
                            )}
                            {voucher.remaining_value !== undefined && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Pozostało:</span>
                                <span className="font-medium text-right">{voucher.remaining_value} zł</span>
                              </div>
                            )}
                            {voucher.priceDiscrepancy && (
                              <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded-lg">
                                <div className="flex items-start space-x-2">
                                  <span className="text-orange-500 flex-shrink-0">⚠️</span>
                                  <p className="text-xs text-orange-700">
                                    {voucher.priceDiscrepancy.message}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="bg-white rounded-lg p-3">
                          <h4 className="font-semibold text-gray-700 mb-2">Daty i status</h4>
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Utworzony:</span>
                              <span className="font-medium text-right text-xs">{formatDate(voucher.created_at)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Data zakupu:</span>
                              <span className="font-medium text-right text-xs">{formatDate(voucher.purchase_date)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Wygasa:</span>
                              <div className="text-right">
                                <div className="font-medium text-xs">{formatDate(voucher.valid_until)}</div>
                                <div className={`text-xs ${voucher.isExpired ? 'text-red-600' : voucher.daysUntilExpiry <= 7 ? 'text-orange-600' : 'text-green-600'}`}>
                                  ({getDaysText(voucher.daysUntilExpiry)})
                                </div>
                              </div>
                            </div>
                            {voucher.redeemed_at && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Wykorzystany:</span>
                                <span className="font-medium text-right text-xs">{formatDate(voucher.redeemed_at)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex justify-center items-center space-x-4 mt-8">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={!pagination.hasPrevPage}
              className="px-4 py-2 bg-white rounded-lg border border-gray-300 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Poprzednia
            </button>
            
            <span className="text-gray-600">
              Strona {pagination.currentPage} z {pagination.totalPages}
            </span>
            
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={!pagination.hasNextPage}
              className="px-4 py-2 bg-white rounded-lg border border-gray-300 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Następna
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VouchersDatabase;
